import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';

const ForgotPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Forgot Password';
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await authService.forgotPassword(data.email);
      setIsSubmitted(true);
      toast.success('Password reset instructions sent to your email!');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="h-screen flex overflow-hidden">
        {/* Left Side - Full Screen Image (45%) */}
        <div className="w-[45%] relative overflow-hidden">
          <img
            src="/login_side_image.png"
            alt="Healthcare Professional"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Right Side - Success Message (55%) */}
        <div className="w-[55%] flex items-start justify-center bg-white pt-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-xs w-full space-y-6">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-blue-600 mb-4">
                Welcome To
              </h1>
              
              {/* Medi Fusion Logo */}
              <div className="flex justify-center mb-4">
                <img
                  src="/login_medi_logo.png"
                  alt="Medi Fusion"
                  className="h-16 w-auto"
                />
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center space-y-4">
              <div className="flex justify-center mb-4">
                <div className="bg-green-600 p-3 rounded-full">
                  <Mail className="h-8 w-8 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Check Your Email
              </h2>
              <p className="text-gray-600 mb-6">
                We've sent password reset instructions to your registered email address.
              </p>
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600 mb-4">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
              <div className="mt-6">
                <Link
                  to="/auth/login"
                  className="flex items-center justify-center text-sm text-blue-600 hover:text-blue-500"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Full Screen Image (45%) */}
              <div className="w-[45%] relative overflow-hidden h-[115vh]">
          <img
            src="/login_side_image.png"
            alt="Healthcare Professional"
            className="w-full h-full object-cover"
          />
        </div>

      {/* Right Side - Forgot Password Form (55%) */}
      <div className="w-[55%] flex items-start justify-center bg-white pt-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xs w-full space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-blue-600 mb-4">
              Welcome To
            </h1>
            
            {/* Medi Fusion Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/login_medi_logo.png"
                alt="Medi Fusion"
                className="h-16 w-auto"
              />
            </div>
            
            {/* Forgot Password Title */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Forgot Password?
            </h2>
            <p className="text-sm text-gray-600">
              Enter your email address and we'll send a password reset link to your registered email address.
            </p>
          </div>

          {/* Forgot Password Form */}
          <div className="space-y-6">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email:
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address',
                      },
                    })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Send Reset Instructions'
                )}
              </button>
            </form>

            {/* Back to Login */}
            <div className="text-center">
              <Link
                to="/auth/login"
                className="flex items-center justify-center text-sm text-blue-600 hover:text-blue-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 